import React from "react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeader, SiteLayout } from "./components/SiteLayout";
import { solutionPillars } from "./siteData";

const processSteps = [
  { step: "1", title: "Requirements Analysis", description: "We work with you to understand your business goals, technical constraints, and success criteria." },
  { step: "2", title: "Design & Architecture", description: "We design the solution architecture, technology stack, and delivery plan before any code is written." },
  { step: "3", title: "Development & Testing", description: "Our teams build and test through iterative cycles with mentor-led reviews at every stage." },
  { step: "4", title: "Deployment & Support", description: "We deploy with operational clarity and provide documentation and handoff support." },
];

const engagementTypes = [
  { title: "Discovery & Planning", description: "A focused engagement to define requirements, assess technical options, and produce a clear project plan." },
  { title: "Prototype & Pilot", description: "A scoped build to validate the approach before committing to full implementation." },
  { title: "Full Project Implementation", description: "End-to-end delivery from design through deployment, with regular milestones and mentor oversight." },
  { title: "Ongoing Optimization & Support", description: "Post-launch support, system improvements, and technical maintenance to keep your solution running well." },
];

export default function ServicesPage() {
  return (
    <SiteLayout title="Services" description="Software, hardware, and IT engineering services from SaskTech Lab.">
      <PageHero
        eyebrow="Our Services"
        title="Comprehensive engineering solutions delivered by our talented teams under expert guidance."
        description="At SaskTech Lab, we provide engineering services across software, hardware, and IT domains. Our mentorship model combines fresh perspectives with proven expertise, resulting in innovative solutions that meet your specific needs."
        actions={[
          <Link key="contact" to="/contact" className="button-tertiary">
            Start a conversation
          </Link>,
        ]}
      />

      {/* Service pillars */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Capabilities" title="Three service areas, each backed by deep technical expertise." description="We provide high-quality solutions while supporting the development of the next generation of engineering talent." />
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {solutionPillars.map((pillar) => (
            <article key={pillar.title} className="panel-card h-full">
              <h3 className="font-display text-2xl font-bold text-emerald-950">{pillar.title}</h3>
              <p className="mt-4 text-sm leading-7 text-slate-600">{pillar.description}</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                {pillar.bullets.map((bullet) => (
                  <li key={bullet} className="list-bullet-item">{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Process steps */}
      <section className="site-shell section-space">
        <SectionHeader eyebrow="Our Process" title="How we deliver quality at every stage." description="Every project follows a structured delivery process with mentor oversight built in from the start." />
        <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {processSteps.map((item) => (
            <article key={item.title} className="panel-card h-full">
              <p className="font-display text-4xl font-bold text-emerald-200">{item.step}</p>
              <h3 className="mt-4 text-lg font-semibold text-emerald-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Engagement types */}
      <section className="site-shell section-space pb-10">
        <SectionHeader eyebrow="How Engagement Starts" title="Choose the right entry point for your project." description="We can begin with a focused discovery or go straight to full implementation — whatever suits your current needs." />
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {engagementTypes.map((item) => (
            <div key={item.title} className="panel-card">
              <h3 className="font-display text-xl font-bold text-emerald-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/contact" className="button-tertiary">Discuss Your Project</Link>
        </div>
      </section>
    </SiteLayout>
  );
}